import {
  Alert,
  Autocomplete,
  Anchor,
  Badge,
  Button,
  Card,
  Combobox,
  Container,
  createTheme,
  InputBase,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  rem,
  Select,
  Table,
  Textarea,
  TextInput,
  Tooltip
} from '@mantine/core'

import { stepcareButtonRootClassNames } from './components/stepcareButtonTheme'
import { stepcareTextInputStyles } from './components/stepcareInputTheme'

/** Surface chrome for Card/Paper — follows preset + Mantine color scheme */
const surfacePanelRoot = {
  backgroundColor: 'var(--sc-surface-bg)',
  color: 'var(--sc-surface-fg)',
  borderColor: 'var(--sc-surface-border)',
  ...({ '--mantine-color-text': 'var(--sc-surface-fg)' } as Record<
    string,
    string
  >)
}

const CONTAINER_SIZES: Record<string, string> = {
  '2xs': rem(200),
  xs: rem(300),
  sm: rem(500),
  md: rem(600),
  lg: rem(700),
  xl: rem(800),
  '2xl': rem(1200)
}

export const mantineTheme = createTheme({
  breakpoints: {
    xs: '30em',
    sm: '48em',
    md: '64em',
    lg: '74em',
    xl: '90em'
  },
  fontSmoothing: true,
  components: {
    /*
     * Mantine’s Alert.module.css forces .message to --mantine-color-black in
     * light mode (except variant="filled"). Root already uses --alert-color from
     * the color prop — inherit so title/body text follows the chosen color.
     */
    Alert: Alert.extend({
      styles: {
        message: { color: 'inherit' },
        label: { color: 'inherit' }
      }
    }),

    Button: Button.extend({
      defaultProps: {
        radius: 'xl'
      },
      classNames: (theme, props) => ({
        root: stepcareButtonRootClassNames(theme, props)
      }),
      styles: (theme, props) => {
        const labelBase = {
          width: '100%',
          textTransform: 'none' as const,
          gap: '0.3rem'
        }

        /*
         * Mantine outline sets label/section from `color` (often brand → blue
         * text). Match border when using default primary; keep Mantine colors
         * when `color` is set explicitly (e.g. outline + color="red").
         */
        const chromeFg = 'var(--sc-button-chrome-fg, var(--sc-shell-fg))'

        const outlineUsesPrimaryChrome =
          props.variant === 'outline' &&
          (props.color === undefined || props.color === theme.primaryColor)

        if (outlineUsesPrimaryChrome) {
          const border = 'var(--sc-button-outline-border)'
          const outlineLabel = 'var(--sc-button-outline-chrome-fg, var(--sc-button-chrome-fg))'
          return {
            root: { color: outlineLabel, borderColor: border },
            inner: { color: outlineLabel },
            label: { ...labelBase, color: outlineLabel },
            section: { color: outlineLabel }
          }
        }

        if (props.variant === 'nav') {
          const c = 'var(--sc-shell-fg)'
          return {
            root: { color: c },
            inner: { color: c },
            label: { ...labelBase, color: c },
            section: { color: c }
          }
        }

        if (props.variant === 'navActive') {
          const c = 'var(--sc-nav-item-active-fg)'
          return {
            root: { color: c },
            inner: { color: c },
            label: { ...labelBase, color: c },
            section: { color: c }
          }
        }

        if (props.variant === 'light' || props.variant === 'subtle') {
          const c = chromeFg
          return {
            root: { color: c },
            inner: { color: c },
            label: { ...labelBase, color: c },
            section: { color: c }
          }
        }

        const isBrandCta =
          props.variant === undefined ||
          props.variant === null ||
          props.variant === 'default' ||
          props.variant === 'filled'

        if (isBrandCta) {
          const bg = 'var(--sc-interactive-bg)'
          const fg = 'var(--sc-interactive-fg)'
          const border = 'var(--sc-interactive-border, transparent)'
          return {
            root: {
              backgroundColor: bg,
              color: fg,
              borderColor: border,
              '--button-bg': bg,
              '--button-color': fg,
              '--button-hover': 'var(--sc-interactive-bg-hover)',
              '--button-hover-color': fg
            } as Record<string, string>,
            inner: { color: fg },
            label: { ...labelBase, color: fg },
            section: { color: fg }
          }
        }

        if (props.variant === 'primary') {
          const bg = 'var(--sc-interactive-primary-bg)'
          const fg = 'var(--sc-interactive-primary-fg)'
          return {
            root: {
              backgroundColor: bg,
              color: fg,
              borderColor: 'var(--sc-interactive-primary-border, transparent)',
              '--button-bg': bg,
              '--button-color': fg,
              '--button-hover': 'var(--sc-interactive-primary-bg-hover)',
              '--button-hover-color': fg
            } as Record<string, string>,
            inner: { color: fg },
            label: { ...labelBase, color: fg },
            section: { color: fg }
          }
        }

        return { label: labelBase }
      }
    }),

    Input: InputBase.extend({
      styles: {
        root: {
          border: 'none',
          '&:hover': {
            backgroundColor: 'var(--sc-shell-overlay-light)'
          }
        }
      }
    }),

    TextInput: TextInput.extend({
      defaultProps: {
        radius: 'md'
      },
      styles: stepcareTextInputStyles
    }),

    Textarea: Textarea.extend({
      defaultProps: {
        radius: 'md'
      },
      styles: stepcareTextInputStyles
    }),

    Badge: Badge.extend({
      styles: {
        root: {
          border: '1px solid'
        }
      }
    }),

    Card: Card.extend({
      styles: {
        root: surfacePanelRoot,
        section: { color: 'inherit' }
      }
    }),

    Paper: Paper.extend({
      styles: {
        root: surfacePanelRoot
      }
    }),

    Anchor: Anchor.extend({
      defaultProps: {
        c: '#073d72'
      }
    }),
    Table: Table.extend({
      classNames: {
        thead: 'font-thin'
      }
    }),
    Tooltip: Tooltip.extend({
      classNames: {
        tooltip:
          'bg-foundation-800 dark:bg-foundation-900 text-foreground text-md p-2'
      },
      defaultProps: {
        arrowSize: 10,
        opacity: 0.9
      }
    }),
    Menu: Menu.extend({
      defaultProps: {
        arrowSize: 14
      },
      styles: {
        dropdown: {
          backgroundColor: 'var(--sc-surface-bg)',
          color: 'var(--sc-surface-fg)',
          border: '1px solid var(--sc-surface-border)'
        },
        item: {
          color: 'var(--sc-surface-fg)'
        }
      }
    }),
    Select: Select.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--sc-surface-bg)',
          color: 'var(--sc-surface-fg)',
          border: '1px solid var(--sc-surface-border)',
          ...({
            '--mantine-color-text': 'var(--sc-surface-fg)',
            '--mantine-color-dimmed':
              'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
          } as Record<string, string>)
        },
        option: {
          color: 'var(--sc-surface-fg)'
        }
      }
    }),
    MultiSelect: MultiSelect.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--sc-surface-bg)',
          color: 'var(--sc-surface-fg)',
          border: '1px solid var(--sc-surface-border)',
          ...({
            '--mantine-color-text': 'var(--sc-surface-fg)',
            '--mantine-color-dimmed':
              'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
          } as Record<string, string>)
        },
        option: {
          color: 'var(--sc-surface-fg)'
        },
        pill: {
          color: 'var(--sc-surface-fg)'
        }
      }
    }),
    Autocomplete: Autocomplete.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--sc-surface-bg)',
          color: 'var(--sc-surface-fg)',
          border: '1px solid var(--sc-surface-border)',
          ...({
            '--mantine-color-text': 'var(--sc-surface-fg)',
            '--mantine-color-dimmed':
              'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
          } as Record<string, string>)
        },
        option: {
          color: 'var(--sc-surface-fg)'
        }
      }
    }),
    Combobox: Combobox.extend({
      styles: {
        dropdown: {
          backgroundColor: 'var(--sc-surface-bg)',
          color: 'var(--sc-surface-fg)',
          border: '1px solid var(--sc-surface-border)',
          ...({
            '--mantine-color-text': 'var(--sc-surface-fg)',
            '--mantine-color-dimmed':
              'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
          } as Record<string, string>)
        },
        option: {
          color: 'var(--sc-surface-fg)'
        },
        groupLabel: {
          color: 'color-mix(in srgb, var(--sc-surface-fg) 72%, transparent)'
        },
        search: {
          color: 'var(--sc-surface-fg)'
        },
        empty: {
          color: 'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
        }
      }
    }),
    Container: Container.extend({
      vars: (_, { size, fluid }) => ({
        root: {
          '--container-size': fluid
            ? '100%'
            : size !== undefined && size in CONTAINER_SIZES
              ? CONTAINER_SIZES[size]
              : rem(size)
        }
      })
    }),
    Modal: Modal.extend({
      defaultProps: {
        transitionProps: {
          transition: 'fade',
          duration: 100,
          timingFunction: 'ease-in-out'
        }
      },
      classNames: {
        title: 'text-lg font-normal',
        content: 'font-thin'
      },
      styles: {
        header: {
          backgroundColor: 'transparent'
        },
        title: {
          fontWeight: 400,
          fontSize: '1.8rem',
          color: 'var(--sc-surface-fg)',
          lineHeight: '1.5'
        },
        content: {
          borderRadius: 10,
          backgroundColor: 'var(--sc-surface-bg)',
          color: 'var(--sc-surface-fg)',
          border: '1px solid var(--sc-surface-border)',
          ...({
            '--mantine-color-text': 'var(--sc-surface-fg)',
            '--mantine-color-dimmed':
              'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
          } as Record<string, string>)
        },
        body: {
          color: 'var(--sc-surface-fg)',
          ...({
            '--mantine-color-text': 'var(--sc-surface-fg)',
            '--mantine-color-dimmed':
              'color-mix(in srgb, var(--sc-surface-fg) 52%, transparent)'
          } as Record<string, string>)
        },
        overlay: {
          backgroundOpacity: 0.55,
          blur: 3
        }
      }
    })
  },
  colors: {
    brand: [
      '#d5e4ed',
      '#bdd4e2',
      '#a5c4d8',
      '#8db5ce',
      '#75a5c3',
      '#5d95b9',
      '#4586ae',
      '#2d76a4',
      '#1f5373',
      '#122f42'
    ]
  },
  primaryShade: 7,
  primaryColor: 'brand',
  white: '#ffffff',
  shadows: {
    md: '1px 1px 3px rgba(0, 0, 0, .25)',
    xl: '5px 5px 3px rgba(0, 0, 0, .25)'
  },
  defaultRadius: '6px',
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
    sizes: {
      h1: { fontSize: rem(36) }
    }
  }
})

export default mantineTheme
